import type { PlatformMonthlyEvidence, TokenTaskAssumption } from "../dataSources/tokenArrEvidence";
import type { AiMarketPanel } from "../types";

type PlatformModelId = "driver-blend" | "log-drift" | "lag-1";

export interface TokenVolumeScenario {
  aiPenetrationMultiplier: number;
  agentSharePct: number;
  taskLengthMultiplier: number;
  computeCapacityMultiplier: number;
  annualDemandGrowthPct: number;
  unitPriceChangePct: number;
  priceElasticity: number;
}

export interface TokenVolumeBacktestPoint {
  month: string;
  actual: number;
  predicted: number;
  error: number;
  absoluteError: number;
  absolutePercentageError: number;
  low: number;
  high: number;
  intervalCalibrated: boolean;
  priorResiduals: number;
}

export interface TokenVolumeBacktestModel {
  id: PlatformModelId;
  label: string;
  points: TokenVolumeBacktestPoint[];
  metrics: {
    full: { observations: number; mae: number; rmse: number; mape: number; meanError: number };
    recent6: { observations: number; mae: number; rmse: number; mape: number; meanError: number };
    direct1To12: { observations: number; mae: number; rmse: number; mape: number; meanError: number };
    directRecent6: { observations: number; mae: number; rmse: number; mape: number; meanError: number };
  };
}

export interface TokenVolumeSliceResult {
  monitor: {
    asOf: string;
    latestCompleteMonth: string;
    openRouter: {
      scope: "single-platform";
      latestTokensT: number;
      monthOverMonthPct: number | null;
      yearOverYearPct: number | null;
      history: Array<{ month: string; tokensT: number; openAiTokensT: number; anthropicTokensT: number }>;
      market: (AiMarketPanel & {
        signals: {
          intelligenceVelocityPerMonth: number | null;
          intelligenceAcceleration: number | null;
          tokensPerRequestMonthOverMonthPct: number | null;
          standardCallCostMonthOverMonthPct: number | null;
        };
      }) | null;
    };
    codingAdoption: {
      scope: "package-download-index-not-users";
      codexDownloads: number;
      claudeCodeDownloads: number;
      codexMonthOverMonthPct: number | null;
      claudeMonthOverMonthPct: number | null;
      history: Array<{ month: string; codexDownloads: number; claudeCodeDownloads: number }>;
    };
  };
  estimate: {
    target: "estimated-global-monthly-tokens";
    unit: "trillion-tokens-per-month";
    latest: { low: number; base: number; high: number; workloadTokensT: number; billableTokensT: number; computeEquivalentTokensT: number };
    surfaceMix: Array<{ surface: "chat" | "agent"; tokensT: number; sharePct: number }>;
    taskMix: Array<{ task: string; surface: "chat" | "agent"; tokensT: number; sharePct: number }>;
    warning: string;
  };
  forecast: {
    selectedModel: PlatformModelId;
    status: "research-baseline";
    platform: Array<{ month: string; horizon: number; predictedTokensT: number; low: number; high: number; calibrated: boolean }>;
    structural: Array<{ month: string; horizon: number; low: number; base: number; high: number; computeBinding: boolean }>;
    scenarioImpact: { historicalBacktestLocked: true; explanation: string };
  };
  backtest: {
    primary: TokenVolumeBacktestModel;
    candidates: Record<PlatformModelId, TokenVolumeBacktestModel>;
    skillVsLag1Pct: number | null;
    promotionGate: { passed: boolean; reason: string; minimumOosPoints: number };
    historicalScenarioLocked: true;
  };
  methodology: {
    targetDefinition: string;
    structuralFormula: string;
    platformEstimator: string;
    validation: string;
    intervals: string;
    limitations: string[];
  };
}

const MIN_TRAINING_MONTHS = 6;
const MIN_CALIBRATION_ERRORS = 9;

function median(values: number[]) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[middle] : (sorted[middle - 1] + sorted[middle]) / 2;
}

function quantile(values: number[], probability: number) {
  if (!values.length) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.min(sorted.length - 1, Math.ceil(probability * sorted.length) - 1)];
}

function nextMonth(month: string, amount = 1) {
  const [year, value] = month.split("-").map(Number);
  const date = new Date(Date.UTC(year, value - 1 + amount, 1));
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}`;
}

function growth(current: number, prior: number | undefined) {
  return prior && prior > 0 ? (current / prior - 1) * 100 : null;
}

function recentLogGrowth(values: number[], window = 6) {
  const ratios: number[] = [];
  for (let index = Math.max(1, values.length - window); index < values.length; index += 1) {
    if (values[index] > 0 && values[index - 1] > 0) ratios.push(Math.log(values[index] / values[index - 1]));
  }
  return median(ratios);
}

function modelGrowth(id: PlatformModelId, history: readonly PlatformMonthlyEvidence[]) {
  if (id === "lag-1") return 0;
  const tokens = history.map((row) => row.openRouterTotalTokensT);
  const tokenGrowth = recentLogGrowth(tokens);
  if (id === "log-drift") return Math.max(-0.1, Math.min(0.22, tokenGrowth));
  const coding = history.map((row) => row.codexDownloads + row.claudeCodeDownloads);
  const codingGrowth = recentLogGrowth(coding.filter((value) => value > 0));
  return Math.max(-0.1, Math.min(0.22, tokenGrowth * 0.7 + codingGrowth * 0.3));
}

function predictNext(id: PlatformModelId, history: readonly PlatformMonthlyEvidence[]) {
  const latest = history.at(-1);
  if (!latest) throw new Error("Token platform model has no history.");
  return latest.openRouterTotalTokensT * Math.exp(modelGrowth(id, history));
}

function calculateMetrics(rows: TokenVolumeBacktestPoint[]) {
  return {
    observations: rows.length,
    mae: rows.length ? rows.reduce((sum, point) => sum + point.absoluteError, 0) / rows.length : 0,
    rmse: rows.length ? Math.sqrt(rows.reduce((sum, point) => sum + point.error ** 2, 0) / rows.length) : 0,
    mape: rows.length ? rows.reduce((sum, point) => sum + point.absolutePercentageError, 0) / rows.length : 0,
    meanError: rows.length ? rows.reduce((sum, point) => sum + point.error, 0) / rows.length : 0,
  };
}

function directMultiHorizonPoints(id: PlatformModelId, rows: readonly PlatformMonthlyEvidence[]) {
  const points: TokenVolumeBacktestPoint[] = [];
  for (let origin = MIN_TRAINING_MONTHS; origin < rows.length; origin += 1) {
    let synthetic = rows.slice(0, origin).map((row) => ({ ...row }));
    for (let horizon = 1; horizon <= 12 && origin + horizon - 1 < rows.length; horizon += 1) {
      const predicted = predictNext(id, synthetic);
      const target = rows[origin + horizon - 1];
      const error = predicted - target.openRouterTotalTokensT;
      points.push({
        month: target.month,
        actual: target.openRouterTotalTokensT,
        predicted,
        error,
        absoluteError: Math.abs(error),
        absolutePercentageError: Math.abs(error) / target.openRouterTotalTokensT * 100,
        low: predicted,
        high: predicted,
        intervalCalibrated: false,
        priorResiduals: 0,
      });
      synthetic = [...synthetic, {
        ...synthetic.at(-1)!,
        month: target.month,
        openRouterTotalTokensT: predicted,
      }];
    }
  }
  return points;
}

function backtest(id: PlatformModelId, rows: readonly PlatformMonthlyEvidence[]): TokenVolumeBacktestModel {
  const points: TokenVolumeBacktestPoint[] = [];
  for (let index = MIN_TRAINING_MONTHS; index < rows.length; index += 1) {
    const history = rows.slice(0, index);
    const predicted = predictNext(id, history);
    const actual = rows[index].openRouterTotalTokensT;
    const priorErrors = points.map((point) => point.absoluteError);
    const calibrated = priorErrors.length >= MIN_CALIBRATION_ERRORS;
    const radius = calibrated
      ? quantile(priorErrors, 0.9)
      : Math.sqrt(priorErrors.reduce((sum, error) => sum + error ** 2, 0) / Math.max(1, priorErrors.length));
    const error = predicted - actual;
    points.push({
      month: rows[index].month,
      actual,
      predicted,
      error,
      absoluteError: Math.abs(error),
      absolutePercentageError: Math.abs(error) / actual * 100,
      low: Math.max(0, predicted - radius),
      high: predicted + radius,
      intervalCalibrated: calibrated,
      priorResiduals: priorErrors.length,
    });
  }
  const directPoints = directMultiHorizonPoints(id, rows);
  const recentStart = nextMonth(rows.at(-1)!.month, -5);
  return {
    id,
    label: id === "driver-blend" ? "Token + coding adoption growth blend" : id === "log-drift" ? "Recent log drift" : "Last month",
    points,
    metrics: {
      full: calculateMetrics(points),
      recent6: calculateMetrics(points.slice(-6)),
      direct1To12: calculateMetrics(directPoints),
      directRecent6: calculateMetrics(directPoints.filter((point) => point.month >= recentStart)),
    },
  };
}

function taskTokens(row: TokenTaskAssumption) {
  return row.eligibleUsersM * 1e6 * row.penetration * row.activeRate * row.activeDays
    * row.tasksPerDay * row.tokensPerStep * row.agentSteps / 1e12;
}

function finiteScenario(scenario: TokenVolumeScenario) {
  for (const [key, value] of Object.entries(scenario)) if (!Number.isFinite(value)) throw new Error(`Token volume scenario ${key} must be finite.`);
  if (scenario.aiPenetrationMultiplier <= 0 || scenario.taskLengthMultiplier <= 0 || scenario.computeCapacityMultiplier <= 0) {
    throw new Error("Token volume multipliers must be positive.");
  }
  if (scenario.agentSharePct < 0 || scenario.agentSharePct > 100) throw new Error("Token agent share must be between 0 and 100.");
  if (scenario.unitPriceChangePct <= -100) throw new Error("Token unit price change must be greater than -100%.");
}

export function runTokenVolumeSlice(args: {
  asOf: string;
  platformMonthly: readonly PlatformMonthlyEvidence[];
  aiMarket?: AiMarketPanel | null;
  tasks: readonly TokenTaskAssumption[];
  scenario: TokenVolumeScenario;
}): TokenVolumeSliceResult {
  finiteScenario(args.scenario);
  const rows = args.platformMonthly.filter((row) => row.month < args.asOf.slice(0, 7) && row.openRouterTotalTokensT > 0);
  if (rows.length < 12) throw new Error("Token volume needs at least 12 complete platform months.");
  const latest = rows.at(-1)!;
  const prior = rows.at(-2);
  const priorYear = rows.find((row) => row.month === nextMonth(latest.month, -12));
  const marketHistory = args.aiMarket?.history.filter((row) => row.month <= latest.month) ?? [];
  const latestMarket = marketHistory.at(-1);
  const priorMarket = marketHistory.at(-2);
  const previousMarket = marketHistory.at(-3);
  const intelligenceVelocity = latestMarket?.weightedIntelligenceIndex != null && priorMarket?.weightedIntelligenceIndex != null
    ? latestMarket.weightedIntelligenceIndex - priorMarket.weightedIntelligenceIndex
    : null;
  const priorIntelligenceVelocity = priorMarket?.weightedIntelligenceIndex != null && previousMarket?.weightedIntelligenceIndex != null
    ? priorMarket.weightedIntelligenceIndex - previousMarket.weightedIntelligenceIndex
    : null;
  const candidates = {
    "driver-blend": backtest("driver-blend", rows),
    "log-drift": backtest("log-drift", rows),
    "lag-1": backtest("lag-1", rows),
  } satisfies Record<PlatformModelId, TokenVolumeBacktestModel>;
  const lag = candidates["lag-1"];
  const eligible = ([candidates["driver-blend"], candidates["log-drift"]] as const).filter((candidate) =>
    candidate.metrics.full.observations >= MIN_CALIBRATION_ERRORS
    && candidate.metrics.full.rmse <= lag.metrics.full.rmse
    && candidate.metrics.recent6.rmse <= lag.metrics.recent6.rmse
    && candidate.metrics.direct1To12.rmse <= lag.metrics.direct1To12.rmse
    && candidate.metrics.directRecent6.rmse <= lag.metrics.directRecent6.rmse);
  const robustRmse = (model: TokenVolumeBacktestModel) => (
    model.metrics.full.rmse
    + model.metrics.recent6.rmse
    + model.metrics.direct1To12.rmse
    + model.metrics.directRecent6.rmse
  ) / 4;
  const selected = eligible.sort((left, right) => robustRmse(left) - robustRmse(right))[0] ?? lag;
  const selectedErrors = selected.points.map((point) => point.absoluteError);
  const calibrated = selectedErrors.length >= MIN_CALIBRATION_ERRORS;
  const radius = calibrated ? quantile(selectedErrors, 0.9) : selected.metrics.full.rmse;
  const platformForecast: TokenVolumeSliceResult["forecast"]["platform"] = [];
  let synthetic = [...rows];
  for (let horizon = 1; horizon <= 12; horizon += 1) {
    const predicted = predictNext(selected.id, synthetic);
    platformForecast.push({
      month: nextMonth(latest.month, horizon),
      horizon,
      predictedTokensT: predicted,
      low: Math.max(0, predicted - radius * Math.sqrt(horizon)),
      high: predicted + radius * Math.sqrt(horizon),
      calibrated,
    });
    synthetic = [...synthetic, { ...synthetic.at(-1)!, month: nextMonth(latest.month, horizon), openRouterTotalTokensT: predicted }];
  }

  const rawTasks = args.tasks.map((row) => ({ ...row, rawTokensT: taskTokens(row) }));
  const rawChat = rawTasks.filter((row) => row.surface === "chat").reduce((sum, row) => sum + row.rawTokensT, 0);
  const rawAgent = rawTasks.filter((row) => row.surface === "agent").reduce((sum, row) => sum + row.rawTokensT, 0);
  const rawTotal = rawChat + rawAgent;
  if (!(rawTotal > 0) || !(rawChat > 0) || !(rawAgent > 0)) throw new Error("Token structural task totals are invalid.");
  const desiredAgentShare = args.scenario.agentSharePct / 100;
  const surfaceScale = {
    chat: (1 - desiredAgentShare) * rawTotal / rawChat,
    agent: desiredAgentShare * rawTotal / rawAgent,
  };
  const commonMultiplier = args.scenario.aiPenetrationMultiplier * args.scenario.taskLengthMultiplier;
  const tasks = rawTasks.map((row) => ({
    task: row.task,
    surface: row.surface,
    tokensT: row.rawTokensT * surfaceScale[row.surface] * commonMultiplier,
  }));
  const latentTotal = tasks.reduce((sum, row) => sum + row.tokensT, 0);
  const servedTotal = latentTotal * Math.min(1, args.scenario.computeCapacityMultiplier);
  const servedScale = servedTotal / latentTotal;
  const taskMix = tasks.map((row) => ({ ...row, tokensT: row.tokensT * servedScale, sharePct: row.tokensT / latentTotal * 100 }));
  const surfaceMix = (["chat", "agent"] as const).map((surface) => {
    const tokensT = taskMix.filter((row) => row.surface === surface).reduce((sum, row) => sum + row.tokensT, 0);
    return { surface, tokensT, sharePct: tokensT / servedTotal * 100 };
  });
  const priceMultiplier = (1 + args.scenario.unitPriceChangePct / 100) ** (-args.scenario.priceElasticity);
  const structural: TokenVolumeSliceResult["forecast"]["structural"] = platformForecast.map((point) => {
    const observedTrend = point.predictedTokensT / latest.openRouterTotalTokensT;
    const scenarioTrend = (1 + args.scenario.annualDemandGrowthPct / 100) ** (point.horizon / 12) * priceMultiplier;
    const latent = latentTotal * observedTrend * scenarioTrend;
    const served = latent * Math.min(1, args.scenario.computeCapacityMultiplier);
    return {
      month: point.month,
      horizon: point.horizon,
      low: served * 0.55,
      base: served,
      high: served * 1.8,
      computeBinding: args.scenario.computeCapacityMultiplier < 1,
    };
  });
  const skillVsLag = lag.metrics.full.rmse > 0 ? (1 - selected.metrics.full.rmse / lag.metrics.full.rmse) * 100 : null;

  return {
    monitor: {
      asOf: args.asOf,
      latestCompleteMonth: latest.month,
      openRouter: {
        scope: "single-platform",
        latestTokensT: latest.openRouterTotalTokensT,
        monthOverMonthPct: growth(latest.openRouterTotalTokensT, prior?.openRouterTotalTokensT),
        yearOverYearPct: growth(latest.openRouterTotalTokensT, priorYear?.openRouterTotalTokensT),
        history: rows.map((row) => ({ month: row.month, tokensT: row.openRouterTotalTokensT, openAiTokensT: row.openRouterOpenAiTokensT, anthropicTokensT: row.openRouterAnthropicTokensT })),
        market: args.aiMarket ? {
          ...args.aiMarket,
          history: marketHistory,
          signals: {
            intelligenceVelocityPerMonth: intelligenceVelocity,
            intelligenceAcceleration: intelligenceVelocity != null && priorIntelligenceVelocity != null ? intelligenceVelocity - priorIntelligenceVelocity : null,
            tokensPerRequestMonthOverMonthPct: latestMarket?.publicAppTokensPerRequest != null && priorMarket?.publicAppTokensPerRequest != null
              ? growth(latestMarket.publicAppTokensPerRequest, priorMarket.publicAppTokensPerRequest)
              : null,
            standardCallCostMonthOverMonthPct: latestMarket?.standardCallCostUsd != null && priorMarket?.standardCallCostUsd != null
              ? growth(latestMarket.standardCallCostUsd, priorMarket.standardCallCostUsd)
              : null,
          },
        } : null,
      },
      codingAdoption: {
        scope: "package-download-index-not-users",
        codexDownloads: latest.codexDownloads,
        claudeCodeDownloads: latest.claudeCodeDownloads,
        codexMonthOverMonthPct: growth(latest.codexDownloads, prior?.codexDownloads),
        claudeMonthOverMonthPct: growth(latest.claudeCodeDownloads, prior?.claudeCodeDownloads),
        history: rows.map((row) => ({ month: row.month, codexDownloads: row.codexDownloads, claudeCodeDownloads: row.claudeCodeDownloads })),
      },
    },
    estimate: {
      target: "estimated-global-monthly-tokens",
      unit: "trillion-tokens-per-month",
      latest: { low: servedTotal * 0.55, base: servedTotal, high: servedTotal * 1.8, workloadTokensT: servedTotal, billableTokensT: servedTotal * 0.62, computeEquivalentTokensT: servedTotal * 1.28 },
      surfaceMix,
      taskMix,
      warning: "Global Token volume is a bottom-up low/base/high estimate. OpenRouter is displayed separately as a measured platform window and is not grossed up as market share.",
    },
    forecast: {
      selectedModel: selected.id,
      status: "research-baseline",
      platform: platformForecast,
      structural,
      scenarioImpact: { historicalBacktestLocked: true, explanation: "Penetration, surface mix, task length, price elasticity and compute capacity change only the future structural path." },
    },
    backtest: {
      primary: selected,
      candidates,
      skillVsLag1Pct: skillVsLag,
      promotionGate: {
        passed: selected.id !== "lag-1",
        reason: selected.id !== "lag-1"
          ? "A challenger beat lag-1 on full/recent one-month and pooled direct 1–12-month RMSE."
          : "No challenger beat lag-1 on every one-month and direct 1–12-month gate; production remains the simple baseline.",
        minimumOosPoints: MIN_CALIBRATION_ERRORS,
      },
      historicalScenarioLocked: true,
    },
    methodology: {
      targetDefinition: "Primary backtest target is monthly OpenRouter observed Tokens; global volume is a separate structural estimate.",
      structuralFormula: "eligible users × AI penetration × active rate × active days × tasks/day × Tokens/step × agent steps",
      platformEstimator: "Walk-forward comparison of lag-1, recent log drift, and a Token/coding-download growth blend. Every origin uses only prior complete months.",
      validation: "Champion promotion requires at least nine one-month OOS points and no worse RMSE than lag-1 in full/recent one-month and pooled direct 1–12-month windows.",
      intervals: "A nominal 90% conformal radius is shown only after nine genuine prior OOS residuals; otherwise the range is explicitly uncalibrated RMSE.",
      limitations: [
        "OpenRouter is one routing platform, not the global Token denominator.",
        "npm downloads include CI, upgrades and repeated installs; they are not users.",
        "The global low/base/high band is assumption sensitivity, not a statistical confidence interval.",
        "Cache reads, reasoning Tokens, free usage and bundled subscriptions make workload, billable and compute-equivalent Tokens different quantities.",
      ],
    },
  };
}
